import { useLogs } from "@plutoxyz/react-frame";
import React, { useEffect, useRef, useState } from "react";
import { ChevronUp } from "lucide-react";

interface DebuggerProps {
  onHeightChange: (height: number) => void;
}

const PREBUILT_FILTERS = {
  noPlaywright: "^(?!pw:api).*",
  promptsResponse: "^(?!prompts response received).*",
  cdpResponse: "^(?!cdp response received).*",
  waitingForResponse: "^(?!Waiting for prompts response).*",
  onlyPlaywright: "^pw:api.*",
};

// Keys to combine (excluding 'onlyPlaywright')
const filtersToCombine = Object.keys(PREBUILT_FILTERS).filter(
  (key) => key !== "onlyPlaywright",
);

// Extract the negative lookahead content from each selected filter
const lookaheads = filtersToCombine
  .map((key) => {
    const filterRegex = PREBUILT_FILTERS[key as keyof typeof PREBUILT_FILTERS];
    // Extract the content inside (?!...)
    const match = filterRegex.match(/\^\(\?\!(.*)\)\.\*/);
    return match ? `(?!${match[1]})` : ""; // Return the (?!...) part
  })
  .join(""); // Join them together

// Construct the final regex string
const combinedFilterString = `^${lookaheads}.*`;

const Debugger: React.FC<DebuggerProps> = ({ onHeightChange }) => {
  const logs = useLogs();
  const [filter, setFilter] = useState(
    localStorage.getItem("pluto-filter") || "",
  );
  const [selectedFilter, setSelectedFilter] = useState(
    localStorage.getItem("pluto-selected-filter") || "noFilter",
  );

  useEffect(() => {
    localStorage.setItem("pluto-selected-filter", selectedFilter);
  }, [selectedFilter]);
  const [filteredLogs, setFilteredLogs] = useState(logs);
  useEffect(() => {
    if (filter.trim() === "") {
      setFilteredLogs(logs);
      return;
    }

    try {
      const _filter = new RegExp(filter, "i");
      setFilteredLogs(logs.filter((log) => _filter.test(log)));
    } catch (e) {
      return;
    }
  }, [filter, logs]);

  const filterInputRef = useRef<HTMLInputElement>(null);
  const filterSelectRef = useRef<HTMLSelectElement>(null);
  // Add state to track whether the debugger is minimized - start based on window height
  const [isMinimized, setIsMinimized] = React.useState(
    window.innerHeight < 1500,
  );
  // Add state to track if there are new messages
  const [hasNewMessages, setHasNewMessages] = React.useState(false);
  // Track the last seen message count
  const [lastSeenCount, setLastSeenCount] = React.useState(0);
  // Reference to the log container for auto-scrolling
  const logContainerRef = useRef<HTMLDivElement | null>(null);
  // State to track if we should auto-scroll (when user is at the bottom)
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);

  useEffect(() => {
    onHeightChange(isMinimized ? 60 : 300);
  }, [isMinimized]);

  // Function to toggle the minimized state
  const toggleMinimize = () => {
    const newMinimizedState = !isMinimized;
    setIsMinimized(newMinimizedState);

    if (newMinimizedState) {
      setHasNewMessages(false);
      setLastSeenCount(logs.length);
      setShouldAutoScroll(true); // Reset auto-scroll when maximizing
    }
  };

  // Effect to detect new messages when minimized
  useEffect(() => {
    if (isMinimized && logs.length > lastSeenCount) {
      setHasNewMessages(true);
    }
  }, [logs.length, isMinimized, lastSeenCount]);

  // Effect to auto-scroll to bottom when new logs arrive (only if we should auto-scroll)
  useEffect(() => {
    if (!isMinimized && logContainerRef.current && shouldAutoScroll) {
      const container = logContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  }, [logs.length, isMinimized, shouldAutoScroll]);

  // Handle scroll events to determine if we should auto-scroll
  const handleScroll = () => {
    if (logContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = logContainerRef.current;
      // If we're within 30px of the bottom, enable auto-scroll
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 30;
      setShouldAutoScroll(isNearBottom);
    }
  };

  const updateFilter = (filter: string) => {
    localStorage.setItem("pluto-filter", filter);
    setFilter(filter);
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  };

  const scrollToBottom = () => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  };

  return (
    <div
      className="absolute bottom-0 left-0 w-full py-4 transition-all duration-300 h-80 z-10 bg-white rounded-b-2xl border-b border-gray-100"
      style={{
        height: isMinimized ? "60px" : "300px",
        overflow: "hidden",
      }}
    >
      <div
        className="flex items-center justify-between px-4 w-full flex-shrink-0 cursor-pointer "
        onClick={(e) => {
          if (filterInputRef.current) {
            if (e.target === filterInputRef.current) {
              return;
            }
          }
          if (filterSelectRef.current) {
            if (e.target === filterSelectRef.current) {
              return;
            }
          }
          toggleMinimize();
        }}
      >
        <h1 className="text-[28px] font-semibold leading-9 tracking-[0.34px] flex items-center gap-2 text-text-primary">
          Logs
          <input
            className="border border-gray-300 rounded-md p-1 text-sm"
            placeholder="Filter"
            value={filter}
            ref={filterInputRef}
            onChange={(e) => {
              updateFilter(e.target.value);
            }}
          ></input>
          <select
            className="border border-gray-300 rounded-md p-1 text-sm"
            ref={filterSelectRef}
            value={selectedFilter}
            onChange={(e) => {
              switch (e.target.value) {
                case "noFilter":
                  setSelectedFilter("noFilter");
                  updateFilter("");
                  break;
                case "onlyPlaywright":
                  setSelectedFilter("onlyPlaywright");
                  updateFilter(PREBUILT_FILTERS.onlyPlaywright);
                  break;
                case "noPlaywright":
                  setSelectedFilter("noPlaywright");
                  updateFilter(combinedFilterString);
                  break;
              }
            }}
          >
            <option value="noFilter">No Filter</option>
            <option value="onlyPlaywright">Playwright</option>
            <option value="noPlaywright">No Playwright</option>
          </select>
          <div
            className="w-[15px] h-[15px] rounded-full mt-1 animate-fadeIn text-pluto-green bg-pluto-green"
            style={{
              display: hasNewMessages && isMinimized ? "block" : "none",
              boxShadow:
                hasNewMessages && isMinimized
                  ? "0 0 8px 2px rgba(18, 185, 129, 0.8)"
                  : "none",
              animation:
                hasNewMessages && isMinimized
                  ? "pulse 2s infinite ease-in-out"
                  : "none",
              transition: "all 0.5s ease-in-out, box-shadow 1s ease-in-out",
              opacity: hasNewMessages && isMinimized ? 1 : 0,
            }}
          />
        </h1>

        <ChevronUp
          className={`cursor-pointer transition-all duration-300 text-text-primary ${
            isMinimized ? "rotate-180" : ""
          }`}
          size={24}
        />
      </div>
      {!isMinimized && (
        <div
          ref={logContainerRef}
          className="h-full overflow-y-auto"
          onScroll={handleScroll}
        >
          {filteredLogs.length === 0 ? (
            <div className="text-gray-500 italic p-4 text-left">
              No logs to display
            </div>
          ) : (
            <div className="px-2 pt-2 pb-10 ">
              {filteredLogs.map((log, logIndex) => (
                <div className="py-1 text-left pl-4 text-xs" key={logIndex}>
                  <pre className="whitespace-pre-wrap font-mono m-0 overflow-x-auto text-left">
                    {log}
                  </pre>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Debugger;
